/**
 * Form Validation Module
 *
 * Provides real-time UX validation for auth modal forms.
 * Not just visual: the submit handlers stop on any valid: false from validateForm().
 * A verdict that must not block returns valid: true with notice: true instead.
 *
 * Delegates to shared modules:
 *   - field-validators.js   -> pure validation logic (no DOM)
 *   - field-state-manager.js -> DOM state / error display
 *
 * @package betbeast
 */

import {
    validateEmail      as _validateEmail,
    validatePassword   as _validatePassword,
    validatePhone      as _validatePhone,
    validateBirthdate  as _validateBirthdate,
    validateName       as _validateName,
    validateAddress    as _validateAddress,
    validateCountry    as _validateCountry,
    normalizeEmail     as _normalizeEmail,
    formatPhoneInput   as _formatPhoneInput
} from './field-validators.js';

import {
    phoneCountryVerdict,
    phoneCountryMessage
} from './phone-country-rules.js';

import {
    CLASSES,
    applyFieldState,
    clearFieldState,
    clearAllFieldStates,
    setBackendError    as _setBackendError,
    clearBackendError  as _clearBackendError,
    updateErrorElement,
    scrollToAndFocus
} from './field-state-manager.js';

import { t } from '../blocks/account-i18n.js';

(function () {
    'use strict';

    // Validation rules from PHP (populated via wp_localize_script)
    let rules = {};

    // Field name mapping from backend to frontend field names
    const FIELD_NAME_MAP = {
        'email':        ['email'],
        'password':     ['password'],
        'country':      ['country'],
        'phone':        ['mobile_phone', 'phone'],
        'date_of_birth': ['date_of_birth', 'birthdate'],
        'first_name':   ['first_name', 'firstName'],
        'last_name':    ['last_name', 'lastName'],
        'gender':       ['gender'],
        'city':         ['city'],
        'address':      ['address', 'address1'],
        'postal_code':  ['postal_code', 'postcode', 'zip'],
        'mobile_phone': ['mobile_phone', 'phone']
    };

    // Which surface a form is, for the country phone rule: stricter mode never
    // blocks on the profile confirmation modal (signup-modal-step2).
    const PHONE_SURFACES = {
        'signup-modal':       'signup',
        'signup-modal-step2': 'profile',
        'auth-mobile-phone':  'account'
    };

    // ---- Initialisation -------------------------------------------------------

    function init() {
        if (typeof accountData !== 'undefined' && accountData.validationRules) {
            rules = accountData.validationRules;
        }

        document.addEventListener('DOMContentLoaded', function () {
            initModalFormValidation();
        });

        if (document.readyState === 'complete' || document.readyState === 'interactive') {
            initModalFormValidation();
        }
    }

    function initModalFormValidation() {
        ['#signup-modal', '#signup-modal-step2', '#auth-login-modal', '#auth-forgot-password'].forEach(function (sel) {
            var el = document.querySelector(sel);
            if (el) attachValidation(el);
        });
    }

    // ---- Attach listeners -----------------------------------------------------

    function attachValidation(form) {
        var inputs = form.querySelectorAll('input, select');

        inputs.forEach(function (input) {
            var fieldName = input.name;
            var fieldType = input.type;

            if (fieldType === 'hidden' || fieldType === 'checkbox' || fieldType === 'radio') return;
            if (input.classList.contains('date-input-hidden')) return;

            input.addEventListener('blur', function () {
                // Only a field the user actually engaged with earns a verdict. The modal
                // autofocus (focusFirstInput in modal-manager.js) focuses the first input
                // on open, so a blur can arrive on a field nobody ever typed in. That
                // flagged an empty Email as invalid, and — worse — the verdict inserts the
                // error message IN FLOW (getOrCreateErrorElement in field-state-manager.js),
                // shoving the "Sign up" link out from under a finger mid-tap: the tap was
                // lost and only the second one switched modals. Already-touched fields keep
                // validating so clearing a filled field still reports "required", and
                // submit-time validateForm() is untouched.
                if (!input.value.trim() && !input.classList.contains(CLASSES.TOUCHED)) {
                    return;
                }

                // A server error stays on its field until the value changes (clearFieldBackendError
                // on input/change) or the form is sent: leaving the field must not re-validate it
                // and wipe the server's message (plan 9.1, condition 2).
                if (input.classList.contains(CLASSES.HAS_BACKEND_ERROR)) {
                    return;
                }

                input.classList.add(CLASSES.TOUCHED);
                validateField(input, form);
            });

            input.addEventListener('input', function () {
                if (input.classList.contains(CLASSES.TOUCHED)) {
                    validateField(input, form);
                }
            });

            if (fieldType === 'email' || fieldName === 'email') {
                input.addEventListener('blur', function () {
                    normalizeEmail(input);
                });
            }

            if (fieldType === 'tel' || fieldName === 'mobile_phone') {
                input.addEventListener('input', function () {
                    formatPhoneInput(input);
                });
            }

            // The country rule follows the dial code, so a new code re-checks the number.
            if (fieldName === 'phone_code' && countryRules()) {
                input.addEventListener('change', function () {
                    var phone = form.querySelector('input[name="mobile_phone"]');
                    if (phone && phone.classList.contains(CLASSES.TOUCHED)) {
                        validateField(phone, form);
                    }
                });
            }
        });

        // DOB composite fields
        form.querySelectorAll('.field-group--dob').forEach(function (fieldGroup) {
            attachDOBValidation(fieldGroup, form);
        });
    }

    // ---- DOB composite field handling -----------------------------------------

    function attachDOBValidation(fieldGroup, form) {
        var dayInput        = fieldGroup.querySelector('.dob-input--day');
        var monthInput      = fieldGroup.querySelector('.dob-input--month');
        var yearInput       = fieldGroup.querySelector('.dob-input--year');
        var hiddenDateInput = fieldGroup.querySelector('.date-input-hidden');

        if (!dayInput || !monthInput || !yearInput || !hiddenDateInput) return;

        var allInputs = [dayInput, monthInput, yearInput];
        var allFields = [
            dayInput.closest('.field'),
            monthInput.closest('.field'),
            yearInput.closest('.field')
        ];

        var dobTouched = false;

        function updateHiddenDate() {
            var day   = dayInput.value.padStart(2, '0');
            var month = monthInput.value.padStart(2, '0');
            var year  = yearInput.value;

            hiddenDateInput.value = (day && month && year && year.length === 4)
                ? year + '-' + month + '-' + day
                : '';
        }

        function validateDOB() {
            if (!dobTouched) return;
            updateHiddenDate();

            var result = validateField(hiddenDateInput, form);

            allFields.forEach(function (field) {
                if (!field) return;
                field.classList.remove(CLASSES.VALID, CLASSES.INVALID, CLASSES.WARNING);
                if (result.valid === null) {
                    // neutral
                } else if (result.valid) {
                    field.classList.add(CLASSES.VALID);
                } else if (result.type === 'warning') {
                    field.classList.add(CLASSES.WARNING);
                } else {
                    field.classList.add(CLASSES.INVALID);
                }
            });

            updateDOBInlineError(fieldGroup, result);
        }

        function isComplete() {
            return dayInput.value.length === 2 &&
                   monthInput.value.length === 2 &&
                   yearInput.value.length === 4;
        }

        allInputs.forEach(function (input) {
            input.addEventListener('input', function () {
                updateHiddenDate();
                if (dobTouched && isComplete()) validateDOB();
            });
            input.addEventListener('blur', function () {
                setTimeout(function () {
                    if (!allInputs.includes(document.activeElement)) {
                        dobTouched = true;
                        allInputs.forEach(function (i) { i.classList.add(CLASSES.TOUCHED); });
                        validateDOB();
                    }
                }, 10);
            });
            input.addEventListener('focus', function () {
                if (!dobTouched) {
                    allFields.forEach(function (f) {
                        if (f) f.classList.remove(CLASSES.VALID, CLASSES.INVALID, CLASSES.WARNING);
                    });
                }
            });
        });
    }

    // ---- Single-field validation (delegates to shared validators) -------------

    function validateField(input, form) {
        var fieldName = input.name;
        var value     = input.value.trim();
        var result    = { valid: true, message: '', type: 'valid' };

        // DOB hidden input — composite completeness checks
        if (fieldName === 'date_of_birth' && input.classList.contains('date-input-hidden')) {
            var dobResult = validateDOBHiddenField(input, value);
            if (dobResult) {
                applyFieldState(input.closest('.field'), dobResult, input);
                return dobResult;
            }
        }

        switch (fieldName) {
            case 'email':
                result = _validateEmail(value, rules.email);
                break;
            case 'password':
                result = _validatePassword(value, rules.password, {
                    minlength: input.hasAttribute('minlength') ? input.getAttribute('minlength') : undefined,
                    maxlength: input.hasAttribute('maxlength') ? input.getAttribute('maxlength') : undefined
                });
                break;
            case 'mobile_phone':
                result = _validatePhone(value, rules.phone);
                if (result.valid && countryRules()) {
                    result = applyCountryRule(modalPhone(form, value), form, result);
                }
                break;
            case 'phone':
                // Account page only, and only while country rules are on: without them
                // the field keeps the default branch it always had.
                if (countryRules()) {
                    result = _validatePhone(value, rules.phone);
                    if (result.valid) {
                        result = applyCountryRule(value, form, result);
                    }
                } else if (input.hasAttribute('required') && !value) {
                    result = { valid: false, message: t('fieldRequired'), type: 'error' };
                }
                break;
            case 'date_of_birth':
                result = _validateBirthdate(value, rules.birthdate);
                break;
            case 'first_name':
            case 'last_name':
                result = _validateName(value, rules.name);
                break;
            case 'address':
                result = _validateAddress(value, 'street', rules.address);
                break;
            case 'city':
                result = _validateAddress(value, 'city', rules.address);
                break;
            case 'postcode':
            case 'postal_code':
                result = _validateAddress(value, 'postal', rules.address);
                break;
            case 'country':
                result = _validateCountry(value, rules.country);
                break;
            default:
                if (input.hasAttribute('required') && !value) {
                    result = { valid: false, message: t('fieldRequired'), type: 'error' };
                }
        }

        applyFieldState(input.closest('.field'), result, input);
        return result;
    }

    // ---- Country phone rule ---------------------------------------------------

    function countryRules() {
        return rules.phone && rules.phone.country_rules ? rules.phone.country_rules : null;
    }

    // The modal number as it will be sent: "+" + dial code + digits (gatherFormData).
    function modalPhone(form, value) {
        var select = form ? form.querySelector('select[name="phone_code"]') : null;
        var code   = select ? select.value : '';
        var digits = value.replace(/\D/g, '');
        return code ? '+' + code + digits : digits;
    }

    // Region to judge a rejected shared-code number for: the flag picked in a modal,
    // the account country on the account page (the server uses the same country).
    function preferredRegion(form, surface) {
        if (surface === 'account') {
            // Rendered into the form from the record the server judges by; accountData is
            // printed by two blocks and only one of them carries a country.
            return form && form.dataset.accountCountry ? form.dataset.accountCountry : '';
        }
        var select = form ? form.querySelector('select[name="phone_code"]') : null;
        var option = select && select.selectedIndex >= 0 ? select.options[select.selectedIndex] : null;
        return option ? (option.getAttribute('data-country') || '').split(':')[0] : '';
    }

    // A flagged number is valid with a notice (it submits), unless stricter mode
    // blocks it on this surface. ok and no verdict keep the result as it was.
    function applyCountryRule(phone, form, result) {
        var surface = form && PHONE_SURFACES[form.id] ? PHONE_SURFACES[form.id] : 'signup';
        var verdict = phoneCountryVerdict(phone, surface, countryRules(), preferredRegion(form, surface));

        if (verdict.status !== 'flagged') {
            return result;
        }

        var message = phoneCountryMessage(verdict, countryRules(), window.COUNTRY_PHONE_DATA);
        return verdict.block
            ? { valid: false, message: message, type: 'error' }
            : { valid: true, notice: true, message: message, type: 'error' };
    }

    /**
     * Handle DOB hidden input completeness checks before the actual date validation.
     * Returns a result object if we should short-circuit, or null to fall through.
     */
    function validateDOBHiddenField(input, value) {
        var dobField = input.closest('.field-group--dob');
        if (!dobField) return null;

        var dayInput   = dobField.querySelector('.dob-input--day');
        var monthInput = dobField.querySelector('.dob-input--month');
        var yearInput  = dobField.querySelector('.dob-input--year');
        if (!dayInput || !monthInput || !yearInput) return null;

        var day   = dayInput.value;
        var month = monthInput.value;
        var year  = yearInput.value;

        var anyTouched = dayInput.classList.contains(CLASSES.TOUCHED) ||
                         monthInput.classList.contains(CLASSES.TOUCHED) ||
                         yearInput.classList.contains(CLASSES.TOUCHED);

        if (!day && !month && !year) {
            return anyTouched
                ? { valid: false, message: 'Date of birth is required.', type: 'error' }
                : { valid: null, message: '', type: 'neutral' };
        }

        if (!day || !month || !year) {
            return { valid: false, message: 'Please complete the date of birth.', type: 'warning' };
        }

        if (year.length < 4) {
            return { valid: false, message: 'Please enter a complete year (4 digits).', type: 'warning' };
        }

        // All parts present — delegate to shared birthdate validator
        return _validateBirthdate(value, rules.birthdate);
    }

    // ---- DOB inline error helper ----------------------------------------------

    function updateDOBInlineError(fieldGroup, result) {
        updateErrorElement(fieldGroup, result);
    }

    // ---- Whole-form validation -------------------------------------------------

    function validateForm(form) {
        var inputs  = form.querySelectorAll('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), select');
        var isValid = true;
        var errors  = [];

        // Mark DOB inputs as touched and validate them
        const dobFields = form.querySelectorAll('.field-group--dob');
        dobFields.forEach(function (fieldGroup) {
            // If this DOB group is inside a hidden conditional container, skip validation —
            // the backend already has the value and profile-handler.js handles this correctly.
            const conditionalContainer = fieldGroup.closest('[data-conditional-field="dob"]');
            if (conditionalContainer && conditionalContainer.style.display === 'none') {
                return;
            }

            const dayInput        = fieldGroup.querySelector('.dob-input--day');
            const monthInput      = fieldGroup.querySelector('.dob-input--month');
            const yearInput       = fieldGroup.querySelector('.dob-input--year');
            const hiddenDateInput = fieldGroup.querySelector('.date-input-hidden');

            if (!dayInput || !monthInput || !yearInput || !hiddenDateInput) return;

            // Mark all DOB inputs as touched
            dayInput.classList.add(CLASSES.TOUCHED);
            monthInput.classList.add(CLASSES.TOUCHED);
            yearInput.classList.add(CLASSES.TOUCHED);
            hiddenDateInput.classList.add(CLASSES.TOUCHED);

            // Update hidden date value
            const day   = dayInput.value.padStart(2, '0');
            const month = monthInput.value.padStart(2, '0');
            const year  = yearInput.value;
            hiddenDateInput.value = (day && month && year && year.length === 4)
                ? year + '-' + month + '-' + day
                : '';

            const result = validateField(hiddenDateInput, form);

            const allFields = [
                dayInput.closest('.field'),
                monthInput.closest('.field'),
                yearInput.closest('.field')
            ];

            allFields.forEach(function (field) {
                if (!field) return;
                field.classList.remove(CLASSES.VALID, CLASSES.INVALID, CLASSES.WARNING);
                if (result.valid === null) { /* neutral */ }
                else if (result.valid)              field.classList.add(CLASSES.VALID);
                else if (result.type === 'warning') field.classList.add(CLASSES.WARNING);
                else                                field.classList.add(CLASSES.INVALID);
            });

            updateDOBInlineError(fieldGroup, result);

            if (!result.valid) {
                isValid = false;
                errors.push({ field: 'date_of_birth', message: result.message, type: result.type });
            }
        });

        inputs.forEach(function (input) {
            if (input.classList.contains('dob-input') || input.classList.contains('date-input-hidden')) return;

            input.classList.add(CLASSES.TOUCHED);
            var result = validateField(input, form);
            if (!result.valid) {
                isValid = false;
                errors.push({ field: input.name, message: result.message, type: result.type });
            }
        });

        return { valid: isValid, errors: errors };
    }

    // ---- Email / phone helpers ------------------------------------------------

    function normalizeEmail(input) {
        _normalizeEmail(input, rules.email);
    }

    function formatPhoneInput(input) {
        _formatPhoneInput(input);
    }

    // ---- Clear all validation -------------------------------------------------

    function clearValidation(form) {
        clearAllFieldStates(form);
    }

    // ---- Backend error handling -----------------------------------------------

    function handleBackendErrors(form, response) {
        if (!form || !response) return false;

        clearBackendErrors(form);

        var errors = response.errors || [];
        if (!errors.length) return false;

        var firstErrorField = null;

        errors.forEach(function (error) {
            var input = findFieldByName(form, error.field);
            if (input) {
                setBackendError(input, error.message);
                if (!firstErrorField) firstErrorField = input;
            }
        });

        if (firstErrorField) scrollToAndFocus(firstErrorField);
        return errors.length > 0;
    }

    function findFieldByName(form, fieldName) {
        var input = form.querySelector('input[name="' + fieldName + '"], select[name="' + fieldName + '"]');
        if (input) return input;

        var mappedNames = FIELD_NAME_MAP[fieldName] || [];
        for (var i = 0; i < mappedNames.length; i++) {
            input = form.querySelector('input[name="' + mappedNames[i] + '"], select[name="' + mappedNames[i] + '"]');
            if (input) return input;
        }

        for (var key in FIELD_NAME_MAP) {
            if (FIELD_NAME_MAP[key].includes(fieldName)) {
                input = form.querySelector('input[name="' + key + '"], select[name="' + key + '"]');
                if (input) return input;
            }
        }

        return null;
    }

    function setBackendError(input, message) {
        var field = input.closest('.field');
        _setBackendError(field, input, message);
        attachClearErrorOnInput(input);
    }

    function attachClearErrorOnInput(input) {
        input.removeEventListener('input', clearFieldBackendError);
        input.removeEventListener('change', clearFieldBackendError);
        input.addEventListener('input', clearFieldBackendError);
        input.addEventListener('change', clearFieldBackendError);
    }

    function clearFieldBackendError(event) {
        var input = event.target;
        var field = input.closest('.field');

        if (!input.classList.contains(CLASSES.HAS_BACKEND_ERROR)) return;

        _clearBackendError(field, input);

        input.removeEventListener('input', clearFieldBackendError);
        input.removeEventListener('change', clearFieldBackendError);

        var form = input.closest('form') || input.closest('.auth-modal__content');
        if (form) validateField(input, form);
    }

    function clearBackendErrors(form) {
        if (!form) return;
        form.querySelectorAll('.field').forEach(function (field) {
            field.classList.remove(CLASSES.ERROR, CLASSES.HAS_BACKEND_ERROR);

            var input = field.querySelector('input, select');
            if (input) {
                input.classList.remove(CLASSES.ERROR, CLASSES.HAS_BACKEND_ERROR);
                input.removeEventListener('input', clearFieldBackendError);
                input.removeEventListener('change', clearFieldBackendError);
            }
        });
    }

    function setFieldError(form, fieldName, message) {
        var input = findFieldByName(form, fieldName);
        if (input) setBackendError(input, message);
    }

    function clearFieldError(form, fieldName) {
        var input = findFieldByName(form, fieldName);
        if (input) {
            var field = input.closest('.field');
            input.classList.remove(CLASSES.ERROR, CLASSES.HAS_BACKEND_ERROR, CLASSES.INVALID);
            if (field) {
                field.classList.remove(CLASSES.ERROR, CLASSES.HAS_BACKEND_ERROR, CLASSES.INVALID);
                var errorEl = field.nextElementSibling;
                if (errorEl && errorEl.classList.contains('field__validation-message')) {
                    errorEl.classList.remove('is-visible');
                    errorEl.textContent = '';
                }
            }
        }
    }

    // ---- Boot -----------------------------------------------------------------

    init();

    // ---- Public API (backwards-compatible) ------------------------------------

    window.FormValidation = {
        validate:            validateField,
        validateForm:        validateForm,
        clearValidation:     clearValidation,
        normalizeEmail:      normalizeEmail,
        handleBackendErrors: handleBackendErrors,
        clearBackendErrors:  clearBackendErrors,
        setFieldError:       setFieldError,
        clearFieldError:     clearFieldError,
        scrollToAndFocus:    scrollToAndFocus
    };

})();